import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import GlobalLoader from "../common/GlobalLoader";
import "../../styles/common/dashboardPage.scss";
import "../../styles/common/dashboardCourseSection.scss";
import "../../styles/common/dashboardOverviewSection.scss";
import DashboardOverviewSection from "../common/DashboardOverviewSection";
import DashboardCourseSection from "../common/DashboardCourseSection";
import DashboardBatchSection from "../common/DashboardBatchSection";
import DashboardTeacherSection from "../common/DashboardTeacherSection";
import CommonTable from "../common/commonTable";
import "../../styles/common/userDashboardComponents.scss";
import TooltipText from "../common/tooltipText";
import ChartCard from "../common/charts/ChartCard";
import BarChart from "../common/charts/BarChart";
import DoughnutChart from "../common/charts/DoughnutChart";
import UserMetricCard from "./UserMetricCard";
import AreaChart from "../common/charts/AreaChart";
import LineChart from "../common/charts/LineChart";
import PieChart from "../common/charts/PieChart";
import PolarAreaChart from "../common/charts/PolarAreaChart";
import RadarChart from "../common/charts/RadarChart";
import {
  AddIcon,
  AddCircleIcon,
  AddCircleOutlineIcon,
  DragIndicatorIcon,
  HomeOutlinedIcon,
  FolderIcon,
  ArrowBackIcon,
  FileIcon,
  ArrowUpwardIcon,
  ArrowDropUpIcon,
  ArrowDropDownIcon,
} from "../../icons";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  getInstitutionDashboard1,
  getInstitutionDashboard2,
  getInstitutionDashboard3,
  getInstitutionDashboard5,
  getInstitutionDashboard6,
  getInstitutionDashboard7,
  getInstitutionDashboard8,
} from "../../helper/apicalls/dashboard";
import {
  getAllInstitutionCourse,
  } from "../../helper/apicalls/institution";

const InstitutionDashboardPage = () => {
  const outletContext = useOutletContext?.() || {};
  const { setHeaderName, setIsShowSearch, setReportBreadcrumbLinks } = outletContext;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from({ length: count }, (_, i) => chartPalette.accents[i % chartPalette.accents.length]);

  const sliceHoverColors = (count) =>
    Array.from({ length: count }, (_, i) => chartPalette.accentsHover[i % chartPalette.accentsHover.length]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [institutionOverallData, setInstitutionOverallData] = useState(null);
  const [institutionCourses, setInstitutionCourses] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [institutionCourseDashboardData, setInstitutionCourseDashboardData] = useState(null);
  const [institutionBatchDashboardData, setInstitutionBatchDashboardData] = useState(null);
  const [teacherDashboardData, setTeacherDashboardData] = useState(null);

  const [userData, setUserData] = useState(null);

  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [batchDetailTab, setBatchDetailTab] = useState("teachers");

  const lastCourseFetchRef = useRef("");
  const lastBatchFetchRef = useRef("");
  const lastTeacherFetchRef = useRef({ teacherId: "", courseKey: "" });
  const lastUsersFetchRef = useRef("");
  const lastUserFetchRef = useRef("");

  const courseKeyParam = params?.courseKey ? String(params.courseKey) : "";
  const batchIdParam = params?.batchId ? String(params.batchId) : "";
  const teacherIdParam = params?.teacherId ? String(params.teacherId) : "";
  const studentIdParam = params?.studentId ? String(params.studentId) : "";

  const viewLevel = useMemo(() => {
    if (teacherIdParam) return "teacherReports";
    if (studentIdParam) return "studentReports";
    if (batchIdParam && location.pathname.endsWith("/reports")) return "batchReports";
    if (batchIdParam) return "batchDetail";
    if (courseKeyParam && location.pathname.endsWith("/batches")) return "batches";
    if (courseKeyParam) return "courseReports";
    return "courses";
  }, [batchIdParam, courseKeyParam, location.pathname, studentIdParam, teacherIdParam]);

  const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const safePct = (num, den) => {
    const n = toNum(num, 0);
    const d = toNum(den, 0);
    if (!d) return 0;
    const raw = (n / d) * 100;
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  };

  const userOverview = userData?.overview || null;
  const userAnalytics = userData?.analytics || null;

  const userCards = useMemo(() => {
    const totalPages = toNum(userOverview?.total_page ?? userOverview?.total_pages ?? 0, 0);
    const completedPages = toNum(userOverview?.total_completed_pages ?? userOverview?.completed_pages ?? 0, 0);
    const totalQuiz = toNum(userOverview?.total_quiz ?? 0, 0);
    const quizCompleted = toNum(userOverview?.total_quiz_completed ?? 0, 0);
    const totalTest = toNum(userOverview?.total_test ?? 0, 0);
    const testCompleted = toNum(userOverview?.total_test_completed ?? 0, 0);
    const streak = toNum(userOverview?.current_streak?.active_streak_count ?? 0, 0);

    const activeRecalls = toNum(userAnalytics?.active_recalls ?? 0, 0);
    const adaptives = toNum(userAnalytics?.adaptives ?? 0, 0);

    return [
      { label: "Total Pages", value: totalPages },
      { label: "Pages Completed", value: completedPages },
      { label: "Total Quizzes", value: totalQuiz },
      { label: "Quizzes Completed", value: quizCompleted },
      { label: "Total Tests", value: totalTest },
      { label: "Tests Completed", value: testCompleted },
      { label: "Active Recalls", value: activeRecalls },
      { label: "Adaptives", value: adaptives },
      { label: "Current Streak", value: streak },
    ];
  }, [userAnalytics, userOverview]);

  const progressBasedSubjects = useMemo(() => {
    const pbm = userOverview?.progress_based_mastery;
    if (!pbm || typeof pbm !== "object") return [];
    return Object.keys(pbm)
      .filter((k) => k && k !== "overall")
      .map((k) => ({ key: k, ...(pbm[k] || {}) }))
      .filter((s) => s && typeof s === "object");
  }, [userOverview]);

  const directProgressBarData = useMemo(() => {
    const labels = progressBasedSubjects.map((s) => s?.name || s?.key || "--");
    const values = progressBasedSubjects.map((s) => toNum(s?.direct?.percentage ?? safePct(s?.direct?.completed, s?.direct?.total), 0));
    return {
      labels,
      datasets: [
        {
          label: "Direct %",
          data: values,
          backgroundColor: chartPalette.solid.mint,
          hoverBackgroundColor: chartPalette.hover.mint,
          borderRadius: 10,
        },
      ],
    };
  }, [chartPalette, progressBasedSubjects]);

  const subjectAnalysisBarData = useMemo(() => {
    const analysis = userAnalytics?.course_subject_analysis;
    if (!analysis || typeof analysis !== "object") {
      return { labels: [], datasets: [] };
    }
    const entries = Object.keys(analysis)
      .filter((k) => k && k !== "overall")
      .map((k) => analysis[k])
      .filter(Boolean);
    return {
      labels: entries.map((e) => e?.name || e?.key || "--"),
      datasets: [
        {
          label: "Subject Mastery %",
          data: entries.map((e) => toNum(e?.percentage ?? 0, 0)),
          backgroundColor: chartPalette.solid.lime,
          hoverBackgroundColor: chartPalette.hover.lime,
          borderRadius: 10,
        },
      ],
    };
  }, [chartPalette, userAnalytics]);

  const subjectCompletionData = useMemo(() => {
    const analysis = userAnalytics?.course_subject_analysis;
    if (!analysis || typeof analysis !== "object") return { labels: [], datasets: [] };
    const entries = Object.keys(analysis)
      .filter((k) => k && k !== "overall")
      .map((k) => analysis[k])
      .filter(Boolean);
    return {
      labels: entries.map((e) => e?.name || e?.key || "--"),
      datasets: [
        {
          label: "Completion %",
          data: entries.map((e) => toNum(e?.percentage, 0)),
          backgroundColor: chartPalette.solid.yellow,
          hoverBackgroundColor: chartPalette.hover.yellow,
          borderRadius: 8,
          barThickness: 40,
        },
      ],
    };
  }, [chartPalette, userAnalytics]);

  const timeSpentData = useMemo(() => {
    const overall = userAnalytics?.total_time_spent?.overall?.time_spent || [];
    return {
      labels: overall.map((item) => item.date || ""),
      datasets: [
        {
          label: "Minutes",
          data: overall.map((item) => toNum(item.time, 0)),
          borderColor: chartPalette.solid.orange,
          backgroundColor: chartPalette.soft.orange,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: chartPalette.solid.orange,
          pointHoverBackgroundColor: chartPalette.hover.orange,
          pointBorderColor: "rgba(15, 23, 42, 1)",
          pointHoverBorderColor: "rgba(15, 23, 42, 1)",
        },
      ],
    };
  }, [chartPalette, userAnalytics]);

  const activeRecallMasteryData = useMemo(() => {
    const analysis = userAnalytics?.active_recall_mastery;
    if (!analysis || typeof analysis !== "object") return { labels: [], datasets: [] };
    const entries = Object.keys(analysis)
      .filter((k) => k && k !== "overall")
      .map((k) => ({ name: analysis[k].name, attempted: analysis[k].no_of_questions_attempted, correct: analysis[k].no_of_question_correct }))
      .filter(Boolean);
    
    return {
      labels: entries.map(e => e.name),
      datasets: [
        {
          label: "Attempted",
          data: entries.map(e => toNum(e.attempted, 0)),
          borderColor: chartPalette.solid.orange,
          backgroundColor: chartPalette.soft.orange,
          fill: true,
        },
        {
          label: "Correct",
          data: entries.map(e => toNum(e.correct, 0)),
          borderColor: chartPalette.solid.mint,
          backgroundColor: chartPalette.soft.mint,
          fill: true,
        }
      ]
    };
  }, [chartPalette, userAnalytics]);

  const adaptiveLearningPieData = useMemo(() => {
    const overall = userAnalytics?.adaptive_learning_mastery?.overall || {};
    const correct = toNum(overall.no_of_question_correct, 0);
    const totalAttempted = toNum(overall.no_of_questions_attempted, 0);
    const incorrect = Math.max(0, totalAttempted - correct);

    if (totalAttempted === 0) {
      return {
        labels: ["No data"],
        datasets: [{
          data: [1],
          backgroundColor: [chartPalette.solid.slate],
          hoverBackgroundColor: [chartPalette.hover.slate],
          borderColor: "rgba(15, 23, 42, 1)",
          borderWidth: 2,
        }]
      };
    }

    return {
      labels: ["Correct", "Incorrect"],
      datasets: [{
        data: [correct, incorrect],
        backgroundColor: [chartPalette.solid.mint, chartPalette.solid.slate],
        hoverBackgroundColor: [chartPalette.hover.mint, chartPalette.hover.slate],
        borderColor: "rgba(15, 23, 42, 1)",
        borderWidth: 2,
      }]
    };
  }, [chartPalette, userAnalytics]);

  const quizMasteryPieData = useMemo(() => {
    const overall = userAnalytics?.quiz_completed_mastery?.overall || {};
    const correct = toNum(overall.no_of_question_correct, 0);
    const totalAttempted = toNum(overall.no_of_questions_attempted, 0);
    const incorrect = Math.max(0, totalAttempted - correct);

    if (totalAttempted === 0) {
      return {
        labels: ["No data"],
        datasets: [{
          data: [1],
          backgroundColor: [chartPalette.solid.slate],
          hoverBackgroundColor: [chartPalette.hover.slate],
          borderColor: "rgba(15, 23, 42, 1)",
          borderWidth: 2,
        }]
      };
    }

    return {
      labels: ["Correct", "Incorrect"],
      datasets: [{
        data: [correct, incorrect],
        backgroundColor: [chartPalette.solid.mint, chartPalette.solid.slate],
        hoverBackgroundColor: [chartPalette.hover.mint, chartPalette.hover.slate],
        borderColor: "rgba(15, 23, 42, 1)",
        borderWidth: 2,
      }]
    };
  }, [chartPalette, userAnalytics]);

  const testMasteryBarData = useMemo(() => {
    const analysis = userAnalytics?.test_completed_mastery;
    if (!analysis || typeof analysis !== "object") return { labels: [], datasets: [] };

    const subjects = Object.keys(analysis).filter((k) => k !== "overall");
    const overall = analysis.overall || {};

    return {
      labels: subjects.map(k => analysis[k].name || k),
      datasets: [
        {
          label: "Attempted",
          data: subjects.map(() => toNum(overall.no_of_questions_attempted, 0)),
          backgroundColor: chartPalette.solid.orange,
          hoverBackgroundColor: chartPalette.hover.orange,
          borderRadius: 4,
        },
        {
          label: "Correct",
          data: subjects.map(() => toNum(overall.no_of_question_correct, 0)),
          backgroundColor: chartPalette.solid.mint,
          hoverBackgroundColor: chartPalette.hover.mint,
          borderRadius: 4,
        }
      ]
    };
  }, [chartPalette, userAnalytics]);

  useEffect(() => {
    if (typeof setHeaderName === "function") {
      if (viewLevel === "batchDetail") {
        const name =
          selectedBatch?.name ||
          selectedBatch?.batch_name ||
          (batchIdParam ? `Batch ${batchIdParam}` : "Batch");
        setHeaderName(name);
      } else {
        setHeaderName("Reports");
      }
    }
    if (typeof setIsShowSearch === "function") {
      setIsShowSearch(false);
    }
  }, [batchIdParam, selectedBatch, setHeaderName, setIsShowSearch, viewLevel]);

  useEffect(() => {
    const fetchInstitutionDashboard = async () => {
      const token = Cookies.get("institution_token");
      if (!token) return;

      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionDashboard1(token);
        setInstitutionOverallData(res || null);

        const courseList =
          res?.courses || res?.lists?.courses || res?.data?.courses || [];
        const normalizedCourses = Array.isArray(courseList) ? courseList : [];
        setInstitutionCourses(normalizedCourses);
      } catch (e) {
        console.error("Failed to load institution dashboard data", e);
        setError("Failed to load institution dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionDashboard();
  }, []);

  useEffect(() => {
    const fetchInstitutionCourseDashboard = async () => {
      if (!courseKeyParam) {
        setInstitutionCourseDashboardData(null);
        setInstitutionBatchDashboardData(null);
        setTeachers([]);
        setTeacherDashboardData(null);
        setStudents([]);
        setSelectedBatch(null);
        setSelectedTeacher(null);
        setSelectedStudent(null);
        return;
      }

      const token = Cookies.get("institution_token");
      if (!token) return;

      if (lastCourseFetchRef.current === courseKeyParam && institutionCourseDashboardData) {
        return;
      }
      lastCourseFetchRef.current = courseKeyParam;

      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionDashboard2(
          token,
          courseKeyParam,
        );
        setInstitutionCourseDashboardData(res || null);
      } catch (e) {
        console.error("Failed to load institution course dashboard data", e);
        setError("Failed to load institution course dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionCourseDashboard();
  }, [courseKeyParam]);

  useEffect(() => {
    const fetchInstitutionBatchDashboard = async () => {
      if (!batchIdParam) {
        setInstitutionBatchDashboardData(null);
        setTeachers([]);
        setTeacherDashboardData(null);
        setStudents([]);
        setSelectedTeacher(null);
        setSelectedStudent(null);
        setUserData(null);
        return;
      }

      const token = Cookies.get("institution_token");
      if (!token) return;

      if (lastBatchFetchRef.current === batchIdParam && institutionBatchDashboardData) {
        return;
      }
      lastBatchFetchRef.current = batchIdParam;

      setLoading(true);
      setError("");
      try {
        const batchRes = await getInstitutionDashboard3(token, batchIdParam);
        setInstitutionBatchDashboardData(batchRes || null);

        const teacherListRaw =
          batchRes?.teachers ||
          batchRes?.teacher_list ||
          batchRes?.lists?.teachers ||
          batchRes?.data?.teachers ||
          batchRes?.data ||
          [];

        const normalizedTeachers = Array.isArray(teacherListRaw)
          ? Array.isArray(teacherListRaw[0])
            ? teacherListRaw[0]
            : teacherListRaw
          : [];

        setTeachers(normalizedTeachers);

        if (lastUsersFetchRef.current !== batchIdParam) {
          lastUsersFetchRef.current = batchIdParam;
          try {
            const usersRes = await getInstitutionDashboard6(token, batchIdParam);
            const studentListRaw =
              usersRes?.users ||
              usersRes?.students ||
              usersRes?.data?.users ||
              usersRes?.data?.students ||
              usersRes?.lists?.users ||
              usersRes?.lists?.students ||
              usersRes ||
              [];

            const normalizedStudents = Array.isArray(studentListRaw)
              ? Array.isArray(studentListRaw[0])
                ? studentListRaw[0]
                : studentListRaw
              : [];

            setStudents(normalizedStudents);
          } catch (e) {
            console.error("Failed to load institution users list", e);
            setStudents([]);
          }
        }
      } catch (e) {
        console.error("Failed to load institution batch dashboard data", e);
        setError("Failed to load institution batch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionBatchDashboard();
  }, [batchIdParam]);

  useEffect(() => {
    const fetchInstitutionUserDashboard = async () => {
      if (!studentIdParam) {
        setUserData(null);
        return;
      }

      const token = Cookies.get("institution_token");
      if (!token) return;

      if (lastUserFetchRef.current === studentIdParam && userData) {
        return;
      }
      lastUserFetchRef.current = studentIdParam;

      setLoading(true);
      setError("");
      try {
        const [overviewRes, analyticsRes] = await Promise.all([
          getInstitutionDashboard7(token, String(studentIdParam)),
          getInstitutionDashboard8(token, String(studentIdParam)),
        ]);
        setUserData({
          overview: overviewRes || null,
          analytics: analyticsRes || null,
        });
      } catch (e) {
        console.error("Failed to load institution user data", e);
        setError("Failed to load user reports");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionUserDashboard();
  }, [studentIdParam]);

  useEffect(() => {
    const fetchInstitutionTeacherDashboard = async () => {
      if (!teacherIdParam) {
        setTeacherDashboardData(null);
        return;
      }

      const token = Cookies.get("institution_token");
      if (!token) return;

      const last = lastTeacherFetchRef.current || { teacherId: "", courseKey: "" };
      if (
        last.teacherId === teacherIdParam &&
        last.courseKey === courseKeyParam &&
        teacherDashboardData
      ) {
        return;
      }
      lastTeacherFetchRef.current = { teacherId: teacherIdParam, courseKey: courseKeyParam };

      setLoading(true);
      setError("");
      try {
        const res = await getInstitutionDashboard5(
          token,
          teacherIdParam,
          courseKeyParam,
        );
        setTeacherDashboardData(res || null);
      } catch (e) {
        console.error("Failed to load institution teacher dashboard data", e);
        setError("Failed to load institution teacher dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionTeacherDashboard();
  }, [teacherIdParam, courseKeyParam]);

  const batchesList = useMemo(() => {
    const raw = institutionCourseDashboardData?.batches || institutionCourseDashboardData?.lists?.batches;
    return Array.isArray(raw) ? raw : [];
  }, [institutionCourseDashboardData]);

  useEffect(() => {
    if (!Array.isArray(institutionCourses) || institutionCourses.length === 0) return;
    if (!courseKeyParam) {
      setSelectedCourse(null);
      return;
    }
    const found = institutionCourses.find((c) => String(c?.key ?? c?.course_key ?? c?.courseKey ?? "") === String(courseKeyParam));
    setSelectedCourse(found || { key: courseKeyParam, name: `Course ${courseKeyParam}` });
  }, [courseKeyParam, institutionCourses]);

  useEffect(() => {
    if (!Array.isArray(batchesList) || batchesList.length === 0) {
      setSelectedBatch(null);
      return;
    }
    if (!batchIdParam) {
      setSelectedBatch(null);
      return;
    }
    const found = batchesList.find((b) => String(b?.id ?? b?.batch_id ?? b?.batchId ?? "") === String(batchIdParam));
    setSelectedBatch(found || { id: batchIdParam, name: `Batch ${batchIdParam}` });
  }, [batchIdParam, batchesList]);

  useEffect(() => {
    if (!teacherIdParam) {
      setSelectedTeacher(null);
      return;
    }
    const found = Array.isArray(teachers)
      ? teachers.find((t) => String(t?.id ?? t?.teacher_id ?? t?.teacherId ?? "") === String(teacherIdParam))
      : null;
    setSelectedTeacher(found || { id: teacherIdParam, name: `Teacher ${teacherIdParam}` });
  }, [teacherIdParam, teachers]);

  useEffect(() => {
    if (!studentIdParam) {
      setSelectedStudent(null);
      return;
    }
    const found = Array.isArray(students)
      ? students.find((s) => String(s?.id ?? s?.user_id ?? s?.student_id ?? "") === String(studentIdParam))
      : null;
    setSelectedStudent(found || { id: studentIdParam, name: `Student ${studentIdParam}` });
  }, [studentIdParam, students]);

  useEffect(() => {
    if (typeof setReportBreadcrumbLinks !== "function") return;

    if (!courseKeyParam) {
      setReportBreadcrumbLinks([]);
      return;
    }

    const courseName =
      selectedCourse?.name ||
      selectedCourse?.course_name ||
      selectedCourse?.title ||
      `Course ${courseKeyParam}`;

    const base = [
      {
        label: courseName,
        path: `/institution/reports/course/${courseKeyParam}/batches`,
        active: !batchIdParam,
      },
    ];

    // On batch detail or any nested batch page, show Course -> BatchName.
    if (batchIdParam) {
      const batchName =
        selectedBatch?.name ||
        selectedBatch?.batch_name ||
        (batchIdParam ? `Batch ${batchIdParam}` : "Batch");

      base.push({
        label: batchName,
        path: `/institution/reports/course/${courseKeyParam}/batch/${batchIdParam}`,
        active: true,
      });
    }

    const links = base;

    setReportBreadcrumbLinks(links);
  }, [
    batchIdParam,
    courseKeyParam,
    setReportBreadcrumbLinks,
    selectedBatch,
    selectedCourse,
    selectedStudent,
    selectedTeacher,
    studentIdParam,
    teacherIdParam,
    viewLevel,
  ]);

  const courseColumns = useMemo(
    () => [
      {
        field: "name",
        label: "Course Name",
        align: "left",
        render: (v, row) => v || row.course_name || row.title || `Course ${row.key || row.course_key || ""}`,
      },
      {
        field: "publish",
        label: "Status",
        align: "left",
        render: (v, row) => {
          const pub = v ?? row?.publish ?? row?.published ?? row?.is_published ?? row?.isPublished;
          if (pub === true) return "Published";
          if (pub === false) return "Not Published";
          return "--";
        },
      },
      {
        field: "actions",
        label: "Actions",
        align: "center",
        render: (_, row) => {
          const key = row?.key ?? row?.course_key ?? row?.courseKey ?? "";
          return (
            <button
              type="button"
              className="dashboard-view-reports-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (key) navigate(`/institution/reports/course/${key}`);
              }}
            >
              <AssessmentIcon fontSize="small" />
              View Reports
            </button>
          );
        },
      },
    ],
    [navigate],
  );

  const batchColumns = useMemo(
    () => [
      {
        field: "name",
        label: "Batch Name",
        align: "left",
        render: (v, row) => v || row.batch_name || `Batch ${row.id ?? row.batch_id ?? row.batchId ?? ""}`,
      },
      {
        field: "active_users",
        label: "Active Users",
        align: "left",
        render: (v, row) =>
          v ??
          row.active_users ??
          row.activeUsers ??
          row.total_active_users ??
          row.totalActiveUsers ??
          "--",
      },
      {
        field: "inactive_users",
        label: "Inactive Users",
        align: "left",
        render: (v, row) =>
          v ??
          row.inactive_users ??
          row.inactiveUsers ??
          row.total_inactive_users ??
          row.totalInactiveUsers ??
          "--",
      },
      {
        field: "total_teachers",
        label: "Total Teachers",
        align: "left",
        render: (v, row) =>
          v ??
          row.total_teachers ??
          row.totalTeachers ??
          row.teacher_count ??
          row.total_teacher ??
          "--",
      },
      {
        field: "actions",
        label: "Actions",
        align: "center",
        render: (_, row) => {
          const id = row?.id ?? row?.batch_id ?? row?.batchId ?? "";
          return (
            <button
              type="button"
              className="dashboard-view-reports-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (courseKeyParam && id) {
                  navigate(`/institution/reports/course/${courseKeyParam}/batch/${id}/reports`);
                }
              }}
            >
              <AssessmentIcon fontSize="small" />
              View Reports
            </button>
          );
        },
      },
    ],
    [courseKeyParam, navigate],
  );

  const handleBatchRowClick = (batch) => {
    const id = batch?.id ?? batch?.batch_id ?? batch?.batchId ?? "";
    if (!courseKeyParam || !id) return;
    setSelectedBatch(batch);
    navigate(`/institution/reports/course/${courseKeyParam}/batch/${id}`);
  };

  const handleTeacherClick = (teacher) => {
    const id = teacher?.id ?? teacher?.teacher_id ?? teacher?.teacherId ?? "";
    if (!courseKeyParam || !batchIdParam || !id) return;
    setSelectedTeacher(teacher);
    navigate(`/institution/reports/course/${courseKeyParam}/batch/${batchIdParam}/teacher/${id}`);
  };

  const handleStudentClick = (student) => {
    const id = student?.id ?? student?.user_id ?? student?.student_id ?? "";
    if (!courseKeyParam || !batchIdParam || !id) return;
    setSelectedStudent(student);
    navigate(`/institution/reports/course/${courseKeyParam}/batch/${batchIdParam}/student/${id}`);
  };

  return (
    <div className="dashboard-page">
      <GlobalLoader visible={loading} text="Loading dashboard..." />
      {!loading && error && <p>{error}</p>}
      {!loading && !error && (
        <>
          {/* <DashboardOverviewSection
            overall={institutionOverallData?.overall}
            showTotalInstitutions={false}
          /> */}

          {viewLevel === "courses" ? (
            <section className="dashboard-section">
              <CommonTable
                data={institutionCourses}
                columns={courseColumns}
                getRowId={(row) => row.key || row.course_key || row.courseKey || Math.random()}
                onRowClick={(row) => {
                  const key = row?.key ?? row?.course_key ?? row?.courseKey ?? "";
                  if (key) navigate(`/institution/reports/course/${key}/batches`);
                }}
                emptyText="No courses available"
                showToolbar={true}
                title="Courses"
                showSearch={true}
                showFilter={false}
              />
            </section>
          ) : null}

          {viewLevel === "courseReports" && selectedCourse && (
            <DashboardCourseSection
              courseOptions={[]}
              selectedCourseOption={{ value: courseKeyParam, label: selectedCourse?.name || `Course ${courseKeyParam}` }}
              onChangeCourse={() => {}}
              courses={[selectedCourse]}
              courseData={institutionCourseDashboardData}
              stats={institutionCourseDashboardData?.stats}
              overall={institutionCourseDashboardData?.overall}
              batches={institutionCourseDashboardData?.batches}
              showSelectRow={false}
            />
          )}

          {viewLevel === "batches" && selectedCourse && (
            <section className="dashboard-section">
              <CommonTable
                data={batchesList}
                columns={batchColumns}
                getRowId={(row) => row?.id ?? row?.batch_id ?? row?.batchId ?? Math.random()}
                onRowClick={handleBatchRowClick}
                emptyText="No batches available"
                showToolbar={true}
                title="Batches"
                showSearch={true}
                showFilter={false}
              />
            </section>
          )}

          {viewLevel === "batchReports" && selectedBatch && (
            <DashboardBatchSection
              batchOptions={[]}
              selectedBatchOption={{ value: batchIdParam, label: selectedBatch?.name || `Batch ${batchIdParam}` }}
              onChangeBatch={() => {}}
              batches={[selectedBatch]}
              batchData={institutionBatchDashboardData}
              showSelectRow={false}
            />
          )}

          {viewLevel === "batchDetail" && selectedBatch && (
            <section className="dashboard-section">
              <div className="dashboard-sticky-tabs">
                <div className="dashboard-filter-tabs-wrap">
                  <div className="dashboard-filter-tabs">
                    <button
                      type="button"
                      className={`dashboard-filter-tab ${batchDetailTab === "teachers" ? "active" : ""}`}
                      onClick={() => setBatchDetailTab("teachers")}
                    >
                      <PersonOutlineOutlinedIcon fontSize="small" style={{ marginRight: 6 }} />
                      Teachers
                    </button>
                    <button
                      type="button"
                      className={`dashboard-filter-tab ${batchDetailTab === "students" ? "active" : ""}`}
                      onClick={() => setBatchDetailTab("students")}
                    >
                      <GroupsOutlinedIcon fontSize="small" style={{ marginRight: 6 }} />
                      Users
                    </button>
                  </div>
                </div>
              </div>

              {batchDetailTab === "teachers" && (
                <div className="dashboard-section">
                  <CommonTable
                    data={teachers}
                    columns={[
                      {
                        field: "name",
                        label: "Name",
                        align: "left",
                        render: (v, row) => v || row.teacher_name || row.full_name || row.email || "--",
                      },
                      {
                        field: "email",
                        label: "Email",
                        align: "left",
                        render: (v, row) => v || row.teacher_email || "--",
                      },
                      {
                        field: "phone",
                        label: "Phone",
                        align: "left",
                        render: (v, row) => v || row.phone || row.teacher_phone || "--",
                      },
                      {
                        field: "actions",
                        label: "Actions",
                        align: "center",
                        render: (_, row) => (
                          <button
                            type="button"
                            className="dashboard-view-reports-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTeacherClick(row);
                            }}
                          >
                            <AssessmentIcon fontSize="small" />
                            View Reports
                          </button>
                        ),
                      },
                    ]}
                    getRowId={(row) => row.id || row.teacher_id || row.teacherId || Math.random()}
                    onRowClick={handleTeacherClick}
                    emptyText="No teachers in this batch"
                    showToolbar={true}
                    title="Teachers"
                    showSearch={true}
                    showFilter={false}
                  />
                </div>
              )}

              {batchDetailTab === "students" && (
                <div className="dashboard-section">
                  <CommonTable
                    data={students}
                    columns={[
                      {
                        field: "name",
                        label: "Name",
                        align: "left",
                        render: (v, row) => v || row.full_name || row.email || "--",
                      },
                      {
                        field: "email",
                        label: "Email",
                        align: "left",
                        render: (v, row) => v || row.email || "--",
                      },
                      {
                        field: "grade",
                        label: "Grade",
                        align: "left",
                        render: (v, row) =>
                          v ??
                          row.grade ??
                          row.class ??
                          row.standard ??
                          row.student_grade ??
                          row.studentGrade ??
                          "--",
                      },
                      {
                        field: "actions",
                        label: "Actions",
                        align: "center",
                        render: (_, row) => (
                          <button
                            type="button"
                            className="dashboard-view-reports-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStudentClick(row);
                            }}
                          >
                            <AssessmentIcon fontSize="small" />
                            View Reports
                          </button>
                        ),
                      },
                    ]}
                    getRowId={(row) => row.id || row.user_id || row.student_id || Math.random()}
                    onRowClick={handleStudentClick}
                    emptyText="No students in this batch"
                    showToolbar={true}
                    title="Students"
                    showSearch={true}
                    showFilter={false}
                  />
                </div>
              )}
            </section>
          )}
          {viewLevel === "teacherReports" && (
            <DashboardTeacherSection
              teacherOptions={[]}
              selectedTeacherOption={{
                value: teacherIdParam,
                label:
                  selectedTeacher?.name ||
                  selectedTeacher?.teacher_name ||
                  selectedTeacher?.full_name ||
                  selectedTeacher?.email ||
                  "Selected Teacher",
              }}
              onChangeTeacher={() => {}}
              teachers={teachers}
              teacherData={teacherDashboardData}
              showSelectRow={false}
            />
          )}

          {viewLevel === "studentReports" && (
            <section className="dashboard-section user-reports-flow">
              {/* Row 1: Metrics (Screenshot 1) */}
              <div className="user-metrics-row">
                <UserMetricCard
                  title="Pages"
                  done={toNum(userOverview?.total_completed_pages ?? userOverview?.completed_pages, 0)}
                  total={toNum(userOverview?.total_page ?? userOverview?.total_pages, 0)}
                  icon={MenuBookIcon}
                  color="blue"
                />
                <UserMetricCard
                  title="Tests"
                  done={toNum(userOverview?.total_test_completed, 0)}
                  total={toNum(userOverview?.total_test, 0)}
                  icon={AssignmentTurnedInIcon}
                  color="green"
                />
                <UserMetricCard
                  title="Quizzes"
                  done={toNum(userOverview?.total_quiz_completed, 0)}
                  total={toNum(userOverview?.total_quiz, 0)}
                  icon={HelpOutlineIcon}
                  color="purple"
                />
              </div>

              {/* Row 2: Progress Based Mastery & Sidebars (Screenshot 2) */}
              <div className="user-mastery-row">
                <div className="user-mastery-card">
                  <div className="mastery-header">
                    <h4>Progress Based Mastery</h4>
                  </div>
                  <div className="mastery-content">
                    <div className="mastery-overall">
                      <DoughnutChart
                        data={{
                          datasets: [
                            {
                              data: [
                                toNum(userOverview?.progress_based_mastery?.overall?.direct?.percentage, 0),
                                Math.max(
                                  0,
                                  100 - toNum(userOverview?.progress_based_mastery?.overall?.direct?.percentage, 0),
                                ),
                              ],
                              backgroundColor: ["#f59e0b", "#f1f5f9"],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{ cutout: "85%", plugins: { legend: { display: false } } }}
                      />
                      <div className="overall-score-label">
                        <span className="score-text">
                          {toNum(userOverview?.progress_based_mastery?.overall?.direct?.percentage, 0)}%
                        </span>
                        <span className="score-desc">Your Mastery Score</span>
                      </div>
                    </div>
                    <div className="mastery-bars">
                      {[
                        { label: "Direct", key: "direct", class: "direct" },
                        { label: "Critical Thinking", key: "critical_thinking", class: "critical" },
                        { label: "Reasoning", key: "reasoning", class: "reasoning" },
                        { label: "Application", key: "application", class: "application" }
                      ].map(bar => (
                        <div key={bar.key} className={`mastery-bar-item ${bar.class}`}>
                          <div className="bar-label-group">
                            <span className="bar-pct">{toNum(userOverview?.progress_based_mastery?.overall?.[bar.key]?.percentage, 0)}%</span>
                            <span className="bar-name">{bar.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="user-sidebar-cards">
                  <div className="user-mini-donut-card">
                    <div className="mini-donut-title">Active Recall</div>
                    <div className="mini-donut-chart-wrap">
                      <div className="mini-donut-chart">
                        <DoughnutChart
                          data={{
                            datasets: [
                              {
                                data: [
                                  toNum(userAnalytics?.active_recall_mastery?.overall?.total_recall_completed, 0),
                                  Math.max(
                                    0,
                                    toNum(userAnalytics?.active_recall_mastery?.overall?.total_recall, 0) -
                                      toNum(userAnalytics?.active_recall_mastery?.overall?.total_recall_completed, 0),
                                  ),
                                ],
                                backgroundColor: ["#3b82f6", "#f1f5f9"],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{ cutout: "80%", plugins: { legend: { display: false } } }}
                        />
                        <div className="donut-center-text">
                          <span className="center-label">Total</span>
                          <span className="center-val">
                            {toNum(userAnalytics?.active_recall_mastery?.overall?.total_recall, 0)}
                          </span>
                        </div>
                      </div>
                      <div className="mini-donut-footer">
                        {toNum(userAnalytics?.active_recall_mastery?.overall?.total_recall_completed, 0)} Completed
                      </div>
                    </div>
                  </div>

                  <div className="user-mini-donut-card">
                    <div className="mini-donut-title">Adaptive Learning</div>
                    <div className="mini-donut-chart-wrap">
                      <div className="mini-donut-chart">
                        <DoughnutChart
                          data={{
                            datasets: [
                              {
                                data: [
                                  toNum(userAnalytics?.adaptive_learning_mastery?.overall?.total_adaptive_learning_completed, 0),
                                  Math.max(
                                    0,
                                    toNum(userAnalytics?.adaptive_learning_mastery?.overall?.total_adaptive_learning, 0) -
                                      toNum(
                                        userAnalytics?.adaptive_learning_mastery?.overall
                                          ?.total_adaptive_learning_completed,
                                        0,
                                      ),
                                  ),
                                ],
                                backgroundColor: ["#3b82f6", "#f1f5f9"],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{ cutout: "80%", plugins: { legend: { display: false } } }}
                        />
                        <div className="donut-center-text">
                          <span className="center-label">Total</span>
                          <span className="center-val">
                            {toNum(userAnalytics?.adaptive_learning_mastery?.overall?.total_adaptive_learning, 0)}
                          </span>
                        </div>
                      </div>
                      <div className="mini-donut-footer">
                        {toNum(userAnalytics?.adaptive_learning_mastery?.overall?.total_adaptive_learning_completed, 0)} Completed
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Course Completion & Total Time Spent */}
              <div className="user-charts-row">
                <ChartCard title="Leaderboard: Course Completion (%)">
                  {subjectCompletionData.labels.length > 0 ? (
                    <BarChart
                      data={subjectCompletionData}
                      options={{
                        responsive: true,
                        scales: { y: { beginAtZero: true, max: 100 } },
                      }}
                    />
                  ) : (
                    <div className="no-data-msg">No subject completion data</div>
                  )}
                </ChartCard>

                <ChartCard
                  title="Leaderboard: Total Time Spent (mins)"
                  footer={(() => {
                    const totalMins =
                      userAnalytics?.total_time_spent?.overall?.time_spent?.reduce(
                        (acc, curr) => acc + toNum(curr.time),
                        0,
                      ) || 0;
                    const hours = Math.floor(totalMins / 60);
                    const mins = totalMins % 60;
                    return (
                      <div className="total-time-header">
                        <span className="time-label">Total time spent</span>
                        <span className="time-value">
                          {hours} Hours {mins} Minutes
                        </span>
                      </div>
                    );
                  })()}
                >
                  {timeSpentData.labels.length > 0 ? (
                    <AreaChart
                      data={timeSpentData}
                      options={{
                        responsive: true,
                        plugins: { legend: { display: false } },
                        scales: { y: { beginAtZero: true } },
                      }}
                    />
                  ) : (
                    <div className="no-data-msg">No time spent data</div>
                  )}
                </ChartCard>
              </div>

              {/* Row 5: Mastery Overviews */}
              <div className="user-mastery-overviews-row">
                <div className="user-mastery-overview-card">
                  <div className="card-header-flex">
                    <h4 className="card-title">Quiz Mastery Overview</h4>
                    {(() => {
                      const overall = userAnalytics?.quiz_completed_mastery?.overall || {};
                      const correct = toNum(overall.no_of_question_correct, 0);
                      const attempted = toNum(overall.no_of_questions_attempted, 0);
                      const accuracy = attempted > 0 ? (correct / attempted) * 100 : 0;
                      return <span className="accuracy-tag">Accuracy {Math.round(accuracy)}%</span>;
                    })()}
                  </div>
                  <div className="chart-container-inner pie-chart-container">
                    <PieChart
                      data={quizMasteryPieData}
                      options={{
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: {
                              usePointStyle: true,
                              pointStyle: "circle",
                              padding: 20,
                              font: { size: 12, weight: "600" },
                            },
                          },
                        },
                        maintainAspectRatio: false,
                      }}
                    />
                  </div>
                </div>

                <div className="user-mastery-overview-card">
                  <div className="card-header-flex">
                    <h4 className="card-title">Test Mastery Overview</h4>
                    {(() => {
                      const overall = userAnalytics?.test_completed_mastery?.overall || {};
                      const correct = toNum(overall.no_of_question_correct, 0);
                      const attempted = toNum(overall.no_of_questions_attempted, 0);
                      const accuracy = attempted > 0 ? (correct / attempted) * 100 : 0;
                      return <span className="accuracy-tag" style={{ background: '#fef3c7', color: '#f59e0b' }}>Accuracy {Math.round(accuracy)}%</span>;
                    })()}
                  </div>
                  <div className="chart-container-inner">
                    <BarChart
                      data={testMasteryBarData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          x: { grid: { display: false } },
                          y: { beginAtZero: true, grid: { color: '#f1f5f9' } }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="user-mastery-overviews-row">
                <div className="user-mastery-overview-card">
                  <div className="card-header-flex">
                    <h4 className="card-title">Active Recall Mastery Overview</h4>
                    {(() => {
                      const overall = userAnalytics?.active_recall_mastery?.overall || {};
                      const correct = toNum(overall.no_of_question_correct, 0);
                      const attempted = toNum(overall.no_of_questions_attempted, 0);
                      const accuracy = attempted > 0 ? (correct / attempted) * 100 : 0;
                      return <span className="accuracy-tag">Accuracy {Math.round(accuracy)}%</span>;
                    })()}
                  </div>
                  <div className="chart-container-inner">
                    {activeRecallMasteryData.labels.length > 0 ? (
                      <AreaChart 
                        data={activeRecallMasteryData} 
                        options={{
                          plugins: { legend: { display: false } },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 11, weight: '600' } } },
                            y: { 
                              beginAtZero: true,
                              grid: { color: '#f1f5f9' }, 
                              ticks: { color: '#94a3b8', font: { size: 11, weight: '600' } } 
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="no-data-msg">No active recall data</div>
                    )}
                  </div>
                </div>

                <div className="user-mastery-overview-card">
                  <div className="card-header-flex">
                    <h4 className="card-title">Adaptive Learning Mastery Overview</h4>
                    {(() => {
                      const overall = userAnalytics?.adaptive_learning_mastery?.overall || {};
                      const correct = toNum(overall.no_of_question_correct, 0);
                      const attempted = toNum(overall.no_of_questions_attempted, 0);
                      const accuracy = attempted > 0 ? (correct / attempted) * 100 : 0;
                      return <span className="accuracy-tag">Accuracy {Math.round(accuracy)}%</span>;
                    })()}
                  </div>
                  <div className="chart-container-inner pie-chart-container">
                    <PieChart
                      data={adaptiveLearningPieData}
                      options={{
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: {
                              usePointStyle: true,
                              pointStyle: "circle",
                              padding: 20,
                              font: { size: 12, weight: "600" },
                            },
                          },
                        },
                        maintainAspectRatio: false,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Row 6: Heatmap */}
              <div className="user-heatmap-card">
                <div className="card-title">Year-Level Overview</div>
                <div className="heatmap-container">
                  <div className="heatmap-days">
                    {["Sun", "", "Tue", "", "Thu", "", "Sat"].map((d, i) => (
                      <span key={i} className="day-label">
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="heatmap-main">
                    <div className="heatmap-months">
                      {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m) => (
                        <span key={m} className="month-label">
                          {m}
                        </span>
                      ))}
                    </div>
                    <div className="heatmap-scroll">
                      <div className="heatmap-grid">
                        {(() => {
                          const yearData = userAnalytics?.year_time_spent || [];
                          const activityMap = {};
                          yearData.forEach((item) => {
                            if (item.date) activityMap[item.date] = toNum(item.time, 0);
                          });

                          const now = new Date();
                          const oneYearAgo = new Date();
                          oneYearAgo.setFullYear(now.getFullYear() - 1);

                          const cols = [];
                          let currentDay = new Date(oneYearAgo);
                          // Move to the first Sunday
                          currentDay.setDate(currentDay.getDate() - currentDay.getDay());

                          for (let i = 0; i < 53; i++) {
                            const days = [];
                            for (let j = 0; j < 7; j++) {
                              const dateStr = currentDay.toISOString().split("T")[0];
                              const timeVal = activityMap[dateStr] || 0;
                              let lvl = 0;
                              if (timeVal > 0 && timeVal < 30) lvl = 1;
                              else if (timeVal >= 30 && timeVal < 60) lvl = 2;
                              else if (timeVal >= 60) lvl = 3;

                              days.push(
                                <div
                                  key={dateStr}
                                  className={`heatmap-cell ${lvl > 0 ? `lvl-${lvl}` : ""}`}
                                  title={`${dateStr}: ${timeVal} mins`}
                                />
                              );
                              currentDay.setDate(currentDay.getDate() + 1);
                            }
                            cols.push(
                              <div key={i} className="heatmap-col">
                                {days}
                              </div>
                            );
                            if (currentDay > now) break;
                          }
                          return cols;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="heatmap-legend">
                  <span>Less</span>
                  <div className="legend-box" />
                  <div className="legend-box lvl-1" />
                  <div className="legend-box lvl-2" />
                  <div className="legend-box lvl-3" />
                  <span>More</span>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default InstitutionDashboardPage;
